import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '@/components/ui/button';
import AppButton from '@/components/ui/AppButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency, formatDate, formatDateTime, cn, getMediaUrl } from '@/lib/utils';
import { Calendar, DollarSign, Trophy, CheckCircle, Clock, AlertCircle, Copy, XCircle, Play, Banknote, CreditCard, ShieldCheck, Zap, Gift, Star, Target, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from '../lib/swal';
import Countdown from '../components/Countdown';
import referralService from '../services/referralService';
import websocketService from '../services/websocket.js';
import SorteoHero from '../components/SorteoHero';
import LiveAnimation from '../components/LiveAnimation';
import FastCheckoutModal from '../components/FastCheckoutModal';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SorteoLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [sorteo, setSorteo] = useState(null);
  const [ganadores, setGanadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cantidad, setCantidad] = useState(1);
  const [numerosBoletos, setNumerosBoletos] = useState(['']);
  const [comprando, setComprando] = useState(false);
  const [validandoBoletos, setValidandoBoletos] = useState(false);
  const [showDatosBancarios, setShowDatosBancarios] = useState(false);
  const [numerosDisponibles, setNumerosDisponibles] = useState([]);
  const [numeroComprobante, setNumeroComprobante] = useState('');
  const [otrosSorteos, setOtrosSorteos] = useState([]);
  const [configuracionAdmin, setConfiguracionAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState("descripcion");

  // Fast Checkout states
  const [fastCheckoutStep, setFastCheckoutStep] = useState(1); // 1: Email, 2: Extra Data, 3: Payment
  const [fastCheckoutEmail, setFastCheckoutEmail] = useState('');
  const [fastCheckoutName, setFastCheckoutName] = useState('');
  const [fastCheckoutCedula, setFastCheckoutCedula] = useState('');
  const [fastCheckoutCelular, setFastCheckoutCelular] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // PayPhone Integration
  const [metodoPago, setMetodoPago] = useState('transferencia');
  const [payphoneSettings, setPayphoneSettings] = useState(null);
  const [clientTxId, setClientTxId] = useState(null);
  const [showPayphoneButton, setShowPayphoneButton] = useState(false);

  // PayPal Integration
  const [paypalSettings, setPaypalSettings] = useState(null);
  const [showPaypalButtons, setShowPaypalButtons] = useState(false);
  const [paypalPurchaseData, setPaypalPurchaseData] = useState(null);

  // Estados para paquetes
  const [paqueteId, setPaqueteId] = useState(new URLSearchParams(window.location.search).get('paquete'));
  const [paqueteData, setPaqueteData] = useState(null);

  const vendedorLink = new URLSearchParams(window.location.search).get('ref');

  // getMediaUrl is now imported from @/lib/utils

  useEffect(() => {
    fetchSorteoData();
    fetchConfiguracion();
    fetchOtrosSorteos();
    fetchPayphoneSettings();
    fetchPaypalSettings();

    // PayPhone SDK v2.0 ahora se carga desde public/index.html para asegurar disponibilidad global

    // Conectar WebSocket y unirse al sorteo
    websocketService.connect();

    return () => {
      if (sorteo) {
        websocketService.leaveSorteo(sorteo.id);
      }
    };
  }, [slug]);

  // Nuevo efecto para manejar la lógica de unión al sorteo una vez que tenemos los datos
  useEffect(() => {
    if (sorteo && sorteo.id) {
      websocketService.joinSorteo(sorteo.id);

      const handleVentasPausadas = (data) => {
        if (data.sorteo_id === sorteo.id) {
          setSorteo(prev => ({
            ...prev,
            ventas_pausadas: data.pausadas
          }));

          if (data.pausadas) {
            toast.warning('Las ventas de este sorteo han sido pausadas temporalmente');
          } else {
            toast.success('¡Las ventas se han reanudado!');
          }
        }
      };

      websocketService.onVentasPausadas(handleVentasPausadas);

      // Escuchar cambios de estado (ej: de waiting a live)
      const handleStateChanged = (data) => {
        if (data.sorteo_id === sorteo.id) {
          console.log(`🚀 Cambio de estado detectado: ${data.new_state}`);
          fetchSorteoData(); // Recargar datos para reflejar el nuevo estado
          
          if (data.new_state === 'live') {
            toast.success('¡El sorteo ha comenzado en vivo!');
          }
        }
      };

      websocketService.onStateChanged(handleStateChanged);

      return () => {
        websocketService.offVentasPausadas(handleVentasPausadas);
        websocketService.offStateChanged(handleStateChanged);
      };
    }
  }, [sorteo?.id]);

  // --- EFECTO CONFETI PARA GANADORES ---
  useEffect(() => {
    if (activeTab === "ganadores" && sorteo?.ganadores?.length > 0) {
      const duration = 30 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 500);

      return () => clearInterval(interval);
    }
  }, [activeTab, sorteo]);

  const fetchSorteoData = async () => {
    try {
      const [sorteoRes, ganadoresRes] = await Promise.all([
        axios.get(`${API}/sorteos/slug/${slug}`),
        axios.get(`${API}/ganadores/sorteo/${slug}`).catch(() => ({ data: [] }))
      ]);

      const sorteoData = sorteoRes.data;
      setSorteo(sorteoData);
      setGanadores(ganadoresRes.data);

      // Obtener números disponibles (necesario tanto para manual como para paquetes para mostrar visualmente)
      const numerosRes = await axios.get(`${API}/sorteos/${sorteoData.id}/numeros-disponibles`);
      const disponibles = numerosRes.data.disponibles;
      setNumerosDisponibles(disponibles);

      const cantidadMinima = sorteoData.cantidad_minima_boletos || 1;
      let cantidadInicial = cantidadMinima;

      // Buscar datos del paquete si existe en la URL
      if (paqueteId && sorteoData.paquetes) {
        const found = sorteoData.paquetes.find(p => p.id === paqueteId);
        if (found) {
          setPaqueteData(found);
          cantidadInicial = found.cantidad;
        }
      }

      setCantidad(cantidadInicial);

      // Asignar números aleatorios iniciales (para visualización)
      if (disponibles && disponibles.length > 0) {
        const disponiblesCopia = [...disponibles];
        const numerosIniciales = [];
        for (let i = 0; i < cantidadInicial && disponiblesCopia.length > 0; i++) {
          const randomIndex = Math.floor(Math.random() * disponiblesCopia.length);
          numerosIniciales.push(disponiblesCopia[randomIndex].toString());
          disponiblesCopia.splice(randomIndex, 1);
        }
        setNumerosBoletos(numerosIniciales);
      } else {
        setNumerosBoletos(Array(cantidadInicial).fill(''));
      }

    } catch (error) {
      console.error('Error al cargar sorteo:', error);
      toast.error('Error al cargar el sorteo');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfiguracion = async () => {
    try {
      const response = await axios.get(`${API}/configuracion-publica`);
      setConfiguracionAdmin(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const fetchOtrosSorteos = async () => {
    try {
      const response = await axios.get(`${API}/sorteos`);
      // Filter published/active raffles excluding the current one
      const activos = response.data.filter(s =>
        (s.estado === 'published' || s.estado === 'activo') && s.landing_slug !== slug
      ).slice(0, 3); // Show max 3 other raffles
      setOtrosSorteos(activos);
    } catch (error) {
      console.error('Error al cargar otros sorteos:', error);
    }
  };

  const fetchPayphoneSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/payphone-settings`);
      setPayphoneSettings(response.data);
    } catch (error) {
      console.error('Error al cargar config payphone:', error);
    }
  };

  const fetchPaypalSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/paypal-settings`);
      setPaypalSettings(response.data);
    } catch (error) {
      console.error('Error al cargar config PayPal:', error);
    }
  };

  const copyDatosBancarios = () => {
    if (!configuracionAdmin) return;
    const texto = `BANCO: ${configuracionAdmin.banco}\nCUENTA: ${configuracionAdmin.numero_cuenta}\nTIPO: ${configuracionAdmin.tipo_cuenta}\nTITULAR: ${configuracionAdmin.nombre_titular}\nCÉDULA/RUC: ${configuracionAdmin.cedula_ruc}\nCORREO: ${configuracionAdmin.correo_pagos}`;
    navigator.clipboard.writeText(texto);
    toast.success('Datos bancarios copiados');
  };

  // NUEVA FUNCIÓN: Asignar números aleatorios disponibles
  const asignarNumerosAleatorios = (cantidad) => {
    if (!numerosDisponibles || numerosDisponibles.length === 0) {
      return Array(cantidad).fill('');
    }

    // Copiar array de disponibles
    const disponiblesCopia = [...numerosDisponibles];
    const numerosAsignados = [];

    for (let i = 0; i < cantidad && disponiblesCopia.length > 0; i++) {
      // Seleccionar índice aleatorio
      const randomIndex = Math.floor(Math.random() * disponiblesCopia.length);
      numerosAsignados.push(disponiblesCopia[randomIndex].toString());
      // Remover el número seleccionado para no repetirlo
      disponiblesCopia.splice(randomIndex, 1);
    }

    // Si no hay suficientes disponibles, rellenar con vacíos
    while (numerosAsignados.length < cantidad) {
      numerosAsignados.push('');
    }

    return numerosAsignados;
  };

  const LIMITE_POR_COMPRA = 500;

  const handleCantidadChange = (nuevaCantidad) => {
    const num = parseInt(nuevaCantidad) || 1;
    const minimo = sorteo?.cantidad_minima_boletos || 1;

    // No permitir menos del mínimo
    if (num < minimo) {
      toast.error(`La compra mínima es de ${minimo} boletos`);
      return;
    }

    // No permitir más del límite por compra
    if (num > LIMITE_POR_COMPRA) {
      toast.error(`El máximo permitido por compra es ${LIMITE_POR_COMPRA} boletos`);
      return;
    }

    setCantidad(num);
    // CAMBIO: Asignar números aleatorios en lugar de vacíos
    const nuevosNumeros = asignarNumerosAleatorios(num);
    setNumerosBoletos(nuevosNumeros);
  };

  const handleNumeroChange = (index, valor) => {
    const nuevosNumeros = [...numerosBoletos];
    nuevosNumeros[index] = valor;
    setNumerosBoletos(nuevosNumeros);
  };

  const validarTodosLosNumeros = async () => {
    const errores = [];

    // Validar cantidad mínima
    const cantidadMinima = sorteo.cantidad_minima_boletos || 1;
    if (numerosBoletos.length < cantidadMinima) {
      errores.push(`Debes comprar al menos ${cantidadMinima} boleto(s)`);
      return errores;
    }

    // Validaciones locales rapidas
    const numerosParseados = [];
    for (let i = 0; i < numerosBoletos.length; i++) {
      const numero = parseInt(numerosBoletos[i]);
      if (!numero) {
        errores.push(`Debe ingresar un número para el boleto ${i + 1}`);
        continue;
      }
      if (numero < 1 || numero > sorteo.cantidad_total_boletos) {
        errores.push(`El boleto ${i + 1} debe estar entre 1 y ${sorteo.cantidad_total_boletos}`);
        continue;
      }
      numerosParseados.push(numero);
    }

    // Validar duplicados localmente
    const setNumeros = new Set();
    for (const num of numerosParseados) {
      if (setNumeros.has(num)) {
        errores.push(`El número ${num} está repetido en la compra`);
      }
      setNumeros.add(num);
    }

    if (errores.length > 0) return errores;

    // Validacion bulk en backend (1 sola llamada)
    try {
      const response = await axios.post(
        `${API}/sorteos/${sorteo.id}/validar-numeros-bulk`,
        { numeros: numerosParseados });

      if (!response.data.todos_disponibles) {
        errores.push(response.data.mensaje);
      }
    } catch (error) {
      console.error('Error al validar números:', error);
      errores.push('Error al validar disponibilidad. Intenta nuevamente.');
    }

    return errores;
  };

  const handleVerDatosBancarios = async () => {
    // Initialize Fast Checkout state
    if (user) {
        setFastCheckoutStep(3); // Already logged in, go straight to payment
    } else {
        setFastCheckoutStep(1); // Not logged in, ask for email
        setFastCheckoutEmail('');
        setFastCheckoutName('');
        setFastCheckoutCedula('');
        setFastCheckoutCelular('');
    }
    
    if (paqueteId) {
      setShowDatosBancarios(true);
      return;
    }

    setValidandoBoletos(true);
    try {
      // Validar todos los números antes de mostrar los datos bancarios
      const errores = await validarTodosLosNumeros();

      if (errores.length > 0) {
        errores.forEach(error => {
          toast.error(error);
        });
        return;
      }

      // Si no hay errores, mostrar el diálogo de datos bancarios
      setShowDatosBancarios(true);
    } catch (error) {
      console.error('Error al validar boletos:', error);
      toast.error('Ocurrio un error al procesar la solicitud. Intenta nuevamente.');
    } finally {
      setValidandoBoletos(false);
    }
  };

  const handleCheckEmail = async () => {
    if (!fastCheckoutEmail || !fastCheckoutEmail.includes('@')) {
      toast.error('Ingresa un correo electrónico válido');
      return;
    }
    setCheckingEmail(true);
    try {
      const response = await axios.post(`${API}/auth/check-email`, { email: fastCheckoutEmail });
      if (response.data.exists) {
        setIsNewUser(false);
        setFastCheckoutStep(3); // Go straight to payment
      } else {
        setIsNewUser(true);
        setFastCheckoutStep(2); // Ask for extra data
      }
    } catch (error) {
      console.error('Error checking email:', error);
      toast.error('Hubo un error al verificar tu correo.');
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleComprar = async () => {
    if (!user && (!fastCheckoutEmail || fastCheckoutStep !== 3)) {
      toast.error('Debes completar el proceso de correo primero');
      return;
    }

    // Validate based on manual or package
    let payload = {
      sorteo_id: sorteo.id,
      metodo_pago: 'transferencia',
      vendedor_id: referralService.getVendedorForPurchase(),
      vendedor_link: vendedorLink,
      numero_comprobante: numeroComprobante
    };
    
    if (!user) {
      payload.fast_checkout_email = fastCheckoutEmail;
      if (isNewUser) {
        payload.fast_checkout_name = fastCheckoutName;
        payload.fast_checkout_cedula = fastCheckoutCedula;
        payload.fast_checkout_celular = fastCheckoutCelular;
      }
    }

    if (paqueteId) {
      payload.paquete_id = paqueteId;
    } else {
      // Validate all numbers are filled
      const numerosValidos = numerosBoletos.filter(n => n.trim() !== '').map(n => parseInt(n));
      if (numerosValidos.length !== cantidad) {
        toast.error('Debes ingresar todos los números de boleto');
        return;
      }

      // Check for duplicates
      const duplicados = numerosValidos.filter((n, i) => numerosValidos.indexOf(n) !== i);
      if (duplicados.length > 0) {
        toast.error(`Números duplicados: ${duplicados.join(', ')}`);
        return;
      }
      payload.numeros_boletos = numerosValidos;
    }

    setComprando(true);
    try {
      const response = await axios.post(`${API}/boletos/comprar`, payload);

      toast.success(response.data.message);

      // Limpiar vendedor después de compra exitosa
      if (payload.vendedor_id) {
        referralService.clearVendedor();
        console.log('✅ Vendedor limpiado después de compra exitosa');
      }

      if (response.data.session_token) {
        localStorage.setItem('ww_session_token', response.data.session_token);
        await checkAuth();
      }

      setShowDatosBancarios(false);
      setCantidad(1);
      setNumerosBoletos(['']);
      setNumeroComprobante('');

      // REDIRIGIR AL PANEL DEL USUARIO - BOLETOS PENDIENTES
      toast.info('Redirigiendo a tu panel para ver tus boletos pendientes...');
      setTimeout(() => {
        navigate('/usuario?tab=boletos');
      }, 1500);

    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('completar tus datos')) {
        toast.error('Debes completar tus datos antes de comprar');
        navigate('/completar-datos');
      } else {
        toast.error(error.response?.data?.detail || 'Error al comprar boletos');
      }
    } finally {
      setComprando(false);
    }
  };

  const handleProceedToPayPhone = async () => {
    if (!user && (!fastCheckoutEmail || fastCheckoutStep !== 3)) {
      toast.error('Debes completar el proceso de correo primero');
      return;
    }

    setComprando(true);
    try {
      let payload = {
        sorteo_id: sorteo.id,
        metodo_pago: 'payphone',
        vendedor_id: referralService.getVendedorForPurchase(),
        vendedor_link: vendedorLink
      };
      
      if (!user) {
        payload.fast_checkout_email = fastCheckoutEmail;
        if (isNewUser) {
          payload.fast_checkout_name = fastCheckoutName;
          payload.fast_checkout_cedula = fastCheckoutCedula;
          payload.fast_checkout_celular = fastCheckoutCelular;
        }
      }

      if (paqueteId) {
        payload.paquete_id = paqueteId;
      } else {
        const numerosValidos = numerosBoletos.filter(n => n.trim() !== '').map(n => parseInt(n));
        payload.numeros_boletos = numerosValidos;
      }

      const response = await axios.post(`${API}/boletos/comprar`, payload);
      const { payphone_data } = response.data;

      if (!payphone_data) {
        toast.error('No se recibió la configuración de PayPhone');
        setComprando(false);
        return;
      }

      // Guardar el clientTransactionId en sessionStorage para que /pago/confirmacion lo recupere
      sessionStorage.setItem('payphone_pending_tx', payphone_data.clientTransactionId);
      setClientTxId(payphone_data.clientTransactionId);
      setShowPayphoneButton(true);

      // Esperar a que el contenedor exista y el SDK esté cargado
      const waitForSdk = (retries = 20) => new Promise((resolve, reject) => {
        const check = () => {
          if (typeof window.PPaymentButtonBox !== 'undefined') {
            resolve();
          } else if (retries-- <= 0) {
            reject(new Error('SDK de PayPhone no se cargó'));
          } else {
            setTimeout(check, 250);
          }
        };
        check();
      });

      setTimeout(async () => {
        const container = document.getElementById('pp-button');
        if (!container) {
          toast.error('Contenedor de PayPhone no encontrado');
          setComprando(false);
          return;
        }
        container.innerHTML = '';

        try {
          await waitForSdk();
          // eslint-disable-next-line no-undef
          new PPaymentButtonBox({
            token: payphone_data.token,
            clientTransactionId: payphone_data.clientTransactionId,
            amount: payphone_data.amount,
            amountWithoutTax: payphone_data.amountWithoutTax,
            amountWithTax: payphone_data.amountWithTax || 0,
            tax: payphone_data.tax || 0,
            service: payphone_data.service || 0,
            tip: payphone_data.tip || 0,
            currency: payphone_data.currency || 'USD',
            storeId: payphone_data.storeId,
            reference: payphone_data.reference,
            lang: payphone_data.lang || 'es',
            defaultMethod: payphone_data.defaultMethod || 'card',
            timeZone: payphone_data.timeZone ?? -5
          }).render('pp-button');
        } catch (err) {
          console.error('Error renderizando PayPhone:', err);
          toast.error('Error al cargar la pasarela de pagos: ' + err.message);
          setComprando(false);
        }
      }, 300);

    } catch (error) {
      console.error('Error al iniciar orden PayPhone:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && detail.includes('completar tus datos')) {
        toast.error('Debes completar tus datos (cédula y celular) antes de comprar');
        navigate('/completar-datos');
        setComprando(false);
        return;
      }
      let msg = 'Error al procesar orden de pago';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail[0]?.msg || msg;
      }
      toast.error(msg);
      setComprando(false);
    }
  };

  const handleProceedToPayPal = async () => {
    if (!user && (!fastCheckoutEmail || fastCheckoutStep !== 3)) {
      toast.error('Debes completar el proceso de correo primero');
      return;
    }

    setComprando(true);
    try {
      let payload = {
        sorteo_id: sorteo.id,
        metodo_pago: 'paypal',
        vendedor_id: referralService.getVendedorForPurchase(),
        vendedor_link: vendedorLink
      };
      
      if (!user) {
        payload.fast_checkout_email = fastCheckoutEmail;
        if (isNewUser) {
          payload.fast_checkout_name = fastCheckoutName;
          payload.fast_checkout_cedula = fastCheckoutCedula;
          payload.fast_checkout_celular = fastCheckoutCelular;
        }
      }

      if (paqueteId) {
        payload.paquete_id = paqueteId;
      } else {
        const numerosValidos = numerosBoletos.filter(n => n.trim() !== '').map(n => parseInt(n));
        payload.numeros_boletos = numerosValidos;
      }

      const response = await axios.post(`${API}/boletos/comprar`, payload);
      const { paypal_data } = response.data;

      setPaypalPurchaseData(paypal_data);
      setShowPaypalButtons(true);

    } catch (error) {
      console.error('Error al iniciar orden PayPal:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('completar tus datos')) {
        toast.error('Debes completar tus datos antes de comprar');
        navigate('/completar-datos');
      } else {
        toast.error(error.response?.data?.detail || 'Error al procesar orden PayPal');
      }
      setComprando(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        purchase_id: paypalPurchaseData.purchase_id,
        total: paypalPurchaseData.total,
        currency: paypalPurchaseData.currency,
        description: paypalPurchaseData.description
      });
      return response.data.order_id;
    } catch (error) {
      console.error('Error creando orden PayPal:', error);
      const detail = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`PayPal: ${detail}`, { duration: 8000 });
      throw error;
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      const response = await axios.post(`${API}/paypal/capture-order/${data.orderID}`);
      if (response.data.status === 'success') {
        toast.success('¡Pago con PayPal confirmado exitosamente!');

        if (referralService.getVendedorForPurchase()) {
          referralService.clearVendedor();
        }

        setShowDatosBancarios(false);
        setShowPaypalButtons(false);
        setPaypalPurchaseData(null);
        setCantidad(1);
        setNumerosBoletos(['']);

        toast.info('Redirigiendo...');
        const montoPaypal = response.data.amount || paypalPurchaseData?.amount;
        const graciasUrl = montoPaypal ? `/gracias?value=${Number(montoPaypal).toFixed(2)}&currency=USD` : '/gracias';
        setTimeout(() => navigate(graciasUrl), 1500);
      } else {
        toast.error(response.data.message || 'El pago no pudo ser completado');
      }
    } catch (error) {
      console.error('Error capturando pago PayPal:', error);
      const detail = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`PayPal: ${detail}`, { duration: 8000 });
    } finally {
      setComprando(false);
    }
  };


  const abrirWhatsApp = () => {
    if (configuracionAdmin?.numero_whatsapp) {
      const mensaje = encodeURIComponent(`Hola, realicé una compra de boleto(s) para el sorteo: ${sorteo.titulo}`);
      window.open(`https://wa.me/${configuracionAdmin.numero_whatsapp.replace(/\D/g, '')}?text=${mensaje}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!sorteo) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-background">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Sorteo no encontrado</h2>
          <Link to="/">
            <Button>Volver al inicio</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const boletosDisponibles = sorteo.cantidad_total_boletos - (sorteo.cantidad_reservada || sorteo.cantidad_vendida);

  return (
    <div className="min-h-screen gradient-background">
      {/* Botón Volver al Inicio */}
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <Link to="/">
          <Button variant="outline" className="mb-4">
            ← Volver al Inicio
          </Button>
        </Link>
      </div>

      {/* ── HERO MULTIMEDIA PREMIUM ── */}
      <SorteoHero sorteo={sorteo} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 🚨 ALERTA DE SORTEO EN VIVO (Bajo el Hero) */}
        {(sorteo.estado === 'live' || sorteo.estado === 'waiting' || sorteo.estado === 'ready' || sorteo.estado === 'drawing') && (
          <div className="w-full mb-12 animate-in slide-in-from-top-4 duration-500">
             <Card className="bg-gradient-to-r from-[#f15a24] to-[#f7931e] border-none shadow-[0_20px_60px_rgba(241,90,36,0.5)] rounded-[2.5rem] overflow-hidden p-8 md:p-12 relative group/live">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 p-12 opacity-10 group-hover/live:scale-110 transition-transform">
                  <Trophy className="w-48 h-48 text-white" />
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between gap-10 relative z-10">
                   <div className="space-y-4 max-w-xl text-center md:text-left">
                      <div className="flex items-center gap-3 justify-center md:justify-start">
                         <div className="w-4 h-4 rounded-full bg-white animate-ping" />
                         <Badge className="bg-white/20 text-white border-none font-black text-[10px] px-4 py-1.5 uppercase tracking-widest italic animate-pulse">¡Transmisión en Vivo!</Badge>
                      </div>
                      <h2 className="text-4xl md:text-6xl font-black text-white italic tracking-tighter uppercase leading-[0.95]">
                        ¡El gran momento <br/> ha <span className="underline decoration-white/40 decoration-wavy underline-offset-8">comenzado</span>!
                      </h2>
                      <p className="text-white/80 font-medium text-lg md:text-xl leading-relaxed">
                        No te pierdas la revelación de los números ganadores. Únete ahora mismo a la sala en directo.
                      </p>
                   </div>
                   
                   <AppButton 
                      size="xl" 
                      className="h-24 px-14 rounded-[2rem] bg-white text-[#f15a24] hover:bg-slate-50 shadow-2xl shadow-black/20 font-black italic uppercase tracking-wider flex items-center gap-5 group/btn transition-all hover:scale-105 active:scale-95"
                      onClick={() => navigate(`/live/${sorteo.id}`)}
                   >
                      <Zap className="w-8 h-8 fill-current group-hover/btn:scale-125 transition-transform" />
                      UNIRSE AL LIVE
                      <ArrowRight className="w-8 h-8 group-hover/btn:translate-x-2 transition-transform" />
                   </AppButton>
                </div>
             </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {sorteo.mostrar_progreso !== false && (
              <Card className="border-none bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-2xl overflow-hidden group" data-testid="progress-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-orange-500/10 dark:bg-orange-500/5 transition-transform group-hover:scale-110">
                      <Trophy className="w-5 h-5 text-orange-500" />
                    </div>
                    <CardTitle className="text-xl font-black uppercase tracking-tighter italic">
                      Progreso del <span className="text-orange-500">Sorteo</span>
                     </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div className="space-y-3">
                    <div className="flex justify-between items-end px-1">
                      <div className="space-y-1">
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Estado de Ventas</p>
                        <p className="text-xl font-black text-slate-800 dark:text-white">
                          {sorteo.cantidad_vendida} <span className="text-sm font-bold opacity-40">/ {sorteo.cantidad_total_boletos} BOLETOS</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-3xl font-black text-orange-500 drop-shadow-sm">
                          {sorteo.progreso_porcentaje.toFixed(1)}<span className="text-lg opacity-60">%</span>
                        </span>
                      </div>
                    </div>
  
                    {/* Premium Progress Bar */}
                    <div className="relative h-4 w-full bg-slate-100 dark:bg-white/5 rounded-lg overflow-hidden border border-slate-200/50 dark:border-white/5 p-0.5">
                      <div
                        className="h-full rounded-md transition-all duration-1000 ease-out relative group-hover:shadow-[0_0_20px_rgba(241,90,36,0.4)]"
                        style={{
                          width: `${sorteo.progreso_porcentaje}%`,
                          background: `linear-gradient(90deg, #f15a24, #f7931e)`,
                          boxShadow: `0 0 15px rgba(241, 90, 36, 0.3)`
                        }}
                      >
                        {/* Animated light effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent w-1/4 animate-[shimmer_2s_infinite]" />
                      </div>
                    </div>
                  </div>
  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-5 bg-blue-500/5 dark:bg-blue-500/5 border border-blue-100/50 dark:border-blue-500/10 rounded-xl transition-all hover:bg-blue-500/10 group/item">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                          <Banknote className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Valor Boleto</span>
                      </div>
                      <p className="text-2xl font-black text-slate-800 dark:text-white">
                        {formatCurrency(sorteo.precio_boleto)}
                      </p>
                    </div>
  
                    <div className="p-5 bg-amber-500/5 dark:bg-amber-500/5 border border-amber-100/50 dark:border-amber-500/10 rounded-xl transition-all hover:bg-amber-500/10">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
                          <Zap className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest">Estado</span>
                      </div>
                      <p className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tighter italic">
                        Ventas Activas ✨
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}


            {sorteo.tipo === 'etapas' && sorteo.etapas.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2 px-2">
                  <div className="p-2.5 rounded-xl bg-orange-500/10 dark:bg-orange-500/5">
                    <Zap className="w-5 h-5 text-orange-500" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter italic">
                    Etapas del <span className="text-orange-500">Sorteo</span>
                  </h2>
                </div>

                <div className="relative pl-4 md:pl-8 space-y-12">
                  {/* Línea de tiempo vertical decorativa */}
                  <div className="absolute left-[23px] md:left-[39px] top-6 bottom-6 w-[0px] border-l-2 border-dashed border-slate-200 dark:border-white/10 opacity-30 z-0" />

                  {sorteo.etapas.map((etapa, idx) => {
                    const ganadoresEtapa = sorteo.ganadores
                      ? sorteo.ganadores.filter(g => g.etapa === etapa.numero || g.etapa_numero === etapa.numero)
                      : [];
                    const tieneGanadores = ganadoresEtapa.length > 0;
                    const etapaCompletada = etapa.completado || tieneGanadores;
                    const progresoActual = sorteo.progreso_porcentaje || 0;
                    const alcanzada = progresoActual >= etapa.porcentaje;

                    // Estilos dinámicos según estado
                    const statusColor = etapaCompletada ? 'text-green-500' : alcanzada ? 'text-orange-500' : 'text-slate-400';
                    const ringColor = etapaCompletada ? 'ring-green-500/30' : alcanzada ? 'ring-orange-500/30' : 'ring-slate-200 dark:ring-white/10';
                    const bgColor = etapaCompletada ? 'bg-green-500/10' : alcanzada ? 'bg-orange-500/10' : 'bg-slate-100 dark:bg-white/5';

                    return (
                      <div key={etapa.numero} className="relative z-10 animate-in fade-in slide-in-from-left-4 duration-500" style={{ animationDelay: `${idx * 150}ms` }}>

                        {/* Punto de la línea de tiempo */}
                        <div className={`absolute -left-[29px] md:-left-[45px] top-2 w-5 h-5 md:w-8 md:h-8 rounded-full ${bgColor} ring-4 ${ringColor} flex items-center justify-center transition-all duration-700`}>
                          {etapaCompletada ? (
                            <CheckCircle className="w-3 h-3 md:w-5 md:h-5 text-green-500" />
                          ) : (
                            <span className={`text-[10px] md:text-xs font-black ${statusColor}`}>{etapa.numero}</span>
                          )}
                        </div>

                        {/* Card de Etapa */}
                        <div className={cn(
                          "p-6 rounded-3xl border transition-all duration-300",
                          etapaCompletada
                            ? "bg-green-500/[0.02] border-green-500/20 shadow-[0_10px_30px_rgba(34,197,94,0.05)]"
                            : alcanzada
                              ? "bg-white dark:bg-slate-900 border-orange-500/30 shadow-[0_15px_40px_rgba(237,95,15,0.1)]"
                              : "bg-white/40 dark:bg-slate-900/40 border-slate-200 dark:border-white/5 opacity-80"
                        )}>

                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                            <div className="space-y-1">
                              <div className="flex items-center gap-3">
                                <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${statusColor}`}>
                                  Etapa {etapa.numero}
                                </span>
                                {alcanzada && !etapaCompletada && (
                                  <Badge className="bg-orange-500 hover:bg-orange-600 text-white border-none text-[8px] font-black uppercase tracking-widest px-2">Activa</Badge>
                                )}
                                {etapaCompletada && (
                                  <Badge className="bg-green-600 hover:bg-green-700 text-white border-none text-[8px] font-black uppercase tracking-widest px-2">Finalizada</Badge>
                                )}
                              </div>
                              <h3 className="text-xl font-black tracking-tight text-slate-800 dark:text-white">
                                {etapa.nombre || etapa.premio}
                              </h3>
                              <div className="flex items-center gap-2">
                                <Target className="w-3 h-3 text-muted-foreground" />
                                <p className="text-[11px] font-medium text-muted-foreground">
                                  Se activa al <span className="font-bold text-foreground">{etapa.porcentaje}%</span> de ventas
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 bg-slate-50 dark:bg-black/20 p-3 rounded-2xl border border-slate-100 dark:border-white/5">
                              <div className="text-right">
                                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Progreso Meta</p>
                                <p className={`text-2xl font-[1000] tracking-tighter ${statusColor}`}>
                                  {etapa.porcentaje}%
                                </p>
                              </div>
                              <div className="h-10 w-[2px] bg-slate-200 dark:bg-white/10" />
                              <div className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alcanzada ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'bg-slate-200 dark:bg-white/10 text-muted-foreground opacity-30'}`}>
                                  <Trophy className="w-4 h-4" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* PREMIOS Y GANADORES (MODERNO) */}
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Star className="w-3 h-3 text-orange-500" />
                              <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Premios y Resultados</span>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                              {(etapa.premios && etapa.premios.length > 0) ? etapa.premios.map((premio, pIdx) => {
                                const ganadorPremio = ganadoresEtapa.find(g =>
                                  g.premio === premio.nombre ||
                                  g.premio === premio.nombre_premio ||
                                  (pIdx === 0 && ganadoresEtapa.length > 0 && !ganadoresEtapa[0].premio)
                                );

                                return (
                                  <div key={pIdx} className={cn(
                                    "group relative flex items-center justify-between p-4 rounded-2xl border transition-all duration-500",
                                    ganadorPremio
                                      ? "bg-green-500/[0.03] border-green-500/20"
                                      : "bg-slate-50/50 dark:bg-white/[0.02] border-slate-100 dark:border-white/5"
                                  )}>
                                    <div className="flex items-center gap-4">
                                      <div className={cn(
                                        "w-12 h-12 rounded-xl flex items-center justify-center border shadow-sm transition-transform duration-500 group-hover:scale-110",
                                        ganadorPremio
                                          ? "bg-green-500/10 border-green-500/20 text-green-500"
                                          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-white/10 text-slate-400"
                                      )}>
                                        {ganadorPremio ? <CheckCircle className="w-6 h-6" /> : <Gift className="w-6 h-6" />}
                                      </div>
                                      <div className="space-y-0.5">
                                        <p className="text-sm font-black text-slate-700 dark:text-white leading-tight">{premio.nombre}</p>
                                        {ganadorPremio ? (
                                          <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                            <p className="text-[11px] font-bold text-green-600 dark:text-green-400">
                                              Ganador: {ganadorPremio.nombre_usuario || ganadorPremio.nombre}
                                            </p>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2 opacity-50">
                                            <Clock className="w-3 h-3" />
                                            <p className="text-[10px] font-bold uppercase tracking-widest">Pendiente de sortear</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {ganadorPremio && (
                                      <div className="hidden sm:block text-right">
                                        <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Boleto Ganador</p>
                                        <Badge className="bg-green-500/10 text-green-600 border-green-500/20 font-black text-xs px-3">
                                          #{ganadorPremio.numero_boleto}
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                );
                              }) : (
                                /* Fallback si no hay premios detallados */
                                <div className="p-4 rounded-2xl bg-slate-50/50 dark:bg-white/[0.02] border-slate-100 dark:border-white/5">
                                  <p className="text-sm font-black text-slate-700 dark:text-white opacity-40 italic">Información de premio no detallada</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sección de Premios */}
            <Card className="border-none bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-2xl overflow-hidden" data-testid="prizes-card">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-orange-500/10 dark:bg-orange-500/5">
                    <Trophy className="w-5 h-5 text-orange-500" />
                  </div>
                  <CardTitle className="text-xl font-black uppercase tracking-tighter italic">
                    Premios del <span className="text-orange-500">Sorteo</span>
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-10">
                {sorteo.tipo === 'etapas' ? (
                  /* Premios agrupados por etapa (PREMIUM VIEW) */
                  <div className="space-y-12">
                    {sorteo.etapas.map((etapa) => (
                      <div key={etapa.numero} className="space-y-6">
                        <div className="flex items-center gap-4">
                          <Badge className="h-8 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-black text-[10px] tracking-widest uppercase border-none shadow-lg shadow-orange-500/20">
                            Etapa {etapa.numero}
                          </Badge>
                          <div className="h-[1px] flex-1 bg-slate-200 dark:bg-white/10" />
                          <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tighter italic">
                            {etapa.nombre || etapa.premio}
                          </h3>
                        </div>

                        {/* TODOS los premios de esta etapa */}
                        {etapa.premios && etapa.premios.length > 0 ? (
                          <div className="grid grid-cols-1 gap-6">
                            {etapa.premios.map((premio, pIdx) => (
                              <div key={pIdx} className="group/premio bg-white/50 dark:bg-slate-800/20 border border-slate-100 dark:border-white/5 rounded-2xl p-6 transition-all hover:bg-white/80 dark:hover:bg-slate-800/40 hover:shadow-xl">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="space-y-1">
                                    <h4 className="font-black text-xl text-slate-900 dark:text-white group-hover/premio:text-orange-500 transition-colors uppercase tracking-tight">{premio.nombre}</h4>
                                    {premio.descripcion && (
                                      <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-xl">{premio.descripcion}</p>
                                    )}
                                  </div>
                                  <div className="p-3 rounded-xl bg-orange-500/5 text-orange-500">
                                    <Trophy className="w-5 h-5" />
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Imagen del premio */}
                                  {(premio.imagen_url || premio.imagen) && (
                                    <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-100 dark:bg-black/20 border border-slate-100 dark:border-white/5">
                                      <img
                                        src={getMediaUrl(premio.imagen_url || premio.imagen)}
                                        alt={premio.nombre}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover/premio:scale-110"
                                      />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                                    </div>
                                  )}

                                  {/* Video del premio (EMBED YouTube) */}
                                  {(premio.video_url || premio.video) && (
                                    <div className="aspect-video rounded-xl overflow-hidden bg-slate-100 dark:bg-black/20 border border-slate-100 dark:border-white/5 shadow-inner">
                                      {(() => {
                                        const videoUrl = premio.video_url || premio.video;
                                        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                                        const match = videoUrl.match(regExp);
                                        const isYouTube = match && match[2].length === 11;

                                        if (isYouTube) {
                                          return (
                                            <iframe
                                              src={`https://www.youtube.com/embed/${match[2]}`}
                                              title={`Video ${premio.nombre}`}
                                              className="w-full h-full"
                                              frameBorder="0"
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                            ></iframe>
                                          );
                                        } else {
                                          return (
                                            <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center space-y-3">
                                              <Play className="w-8 h-8 text-orange-500" />
                                              <a
                                                href={videoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-400 hover:text-orange-500"
                                              >
                                                🎬 Abrir Video Externo
                                              </a>
                                            </div>
                                          );
                                        }
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-8 bg-slate-50 dark:bg-white/5 rounded-2xl border border-dashed border-slate-200 dark:border-white/10 text-center">
                            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">{etapa.premio}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Premios para sorteo normal (único) - PREMIUM GRID */
                  <div className="grid grid-cols-1 gap-10">
                    {sorteo.premios && sorteo.premios.length > 0 ? (
                      sorteo.premios.map((premio, idx) => (
                        <div key={idx} className="group/item space-y-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <h3 className="text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">
                                {premio.nombre}
                              </h3>
                              {premio.descripcion && (
                                <p className="text-base text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-2xl">{premio.descripcion}</p>
                              )}
                            </div>
                            <div className="shrink-0 p-4 rounded-2xl bg-orange-500/10 text-orange-500 transition-transform group-hover/item:rotate-12">
                              <Trophy className="w-6 h-6" />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Imagen del premio con Efecto de Profundidad */}
                            {premio.imagen_url && (
                              <div className="relative group/img aspect-video rounded-3xl overflow-hidden bg-slate-100 dark:bg-black/40 border border-slate-200 dark:border-white/5 shadow-2xl transition-all hover:scale-[1.02]">
                                <img
                                  src={getMediaUrl(premio.imagen_url)}
                                  alt={premio.nombre}
                                  className="w-full h-full object-cover transition-transform duration-1000 group-hover/img:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />
                              </div>
                            )}

                            {/* Video del premio (EMBED) */}
                            {premio.video_url && (
                              <div className="aspect-video rounded-3xl overflow-hidden bg-slate-100 dark:bg-black/40 border border-slate-200 dark:border-white/5 shadow-2xl transition-all hover:scale-[1.02]">
                                <iframe
                                  src={(() => {
                                    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                                    const match = premio.video_url.match(regExp);
                                    return (match && match[2].length === 11)
                                      ? `https://www.youtube.com/embed/${match[2]}?rel=0&modestbranding=1`
                                      : premio.video_url;
                                  })()}
                                  title={`Video ${premio.nombre}`}
                                  className="w-full h-full"
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                ></iframe>
                              </div>
                            )}
                          </div>

                          {idx < sorteo.premios.length - 1 && (
                            <div className="h-[1px] w-full bg-slate-100 dark:bg-white/5" />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="py-20 text-center space-y-4">
                        <Trophy className="w-16 h-16 mx-auto text-slate-200 dark:text-white/10" />
                        <p className="text-slate-400 font-bold uppercase tracking-widest">No hay premios configurados por ahora</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* GALERÍA DE IMÁGENES PROMOCIONALES DEL SORTEO */}
            {sorteo.imagenes && sorteo.imagenes.length > 0 && (
              <Card className="sorteo-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    📸 Imágenes del Sorteo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {sorteo.imagenes.map((imgUrl, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={getMediaUrl(imgUrl)}
                          alt={`${sorteo.titulo} - Imagen ${idx + 1}`}
                          className="w-full h-48 object-cover rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                          onClick={() => window.open(getMediaUrl(imgUrl), '_blank')}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-lg"></div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs defaultValue="descripcion" value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 p-1 bg-slate-100/50 dark:bg-white/5 backdrop-blur-md rounded-2xl mb-8 border border-slate-200 dark:border-white/5 h-14">
                <TabsTrigger 
                  value="descripcion" 
                  className="rounded-xl font-black uppercase text-[10px] tracking-widest data-[state=active]:bg-white dark:data-[state=active]:bg-orange-600 data-[state=active]:text-orange-600 dark:data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
                >
                  Descripción
                </TabsTrigger>
                <TabsTrigger 
                  value="ganadores"
                  className="rounded-xl font-black uppercase text-[10px] tracking-widest data-[state=active]:bg-white dark:data-[state=active]:bg-orange-600 data-[state=active]:text-orange-600 dark:data-[state=active]:text-white data-[state=active]:shadow-lg transition-all"
                >
                  Ganadores {sorteo.ganadores?.length > 0 && <Sparkles className="w-3 h-3 ml-2 animate-pulse" />}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="descripcion">
                <Card className="sorteo-card">
                  <CardHeader>
                    <CardTitle>Detalles del Sorteo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-line break-words overflow-wrap-anywhere">{sorteo.descripcion}</p>
                      {sorteo.reglas && (
                        <div className="mt-6">
                          <h3 className="text-lg font-semibold mb-2">Reglas y Condiciones</h3>
                          <p className="text-gray-700 whitespace-pre-line break-words overflow-wrap-anywhere">{sorteo.reglas}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="ganadores">
                <Card className="border-none shadow-none bg-transparent">
                  <CardContent className="px-0 pt-6">
                    {sorteo.ganadores && sorteo.ganadores.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {sorteo.ganadores.map((ganador, idx) => (
                          <div 
                            key={idx} 
                            className="group relative p-8 rounded-[3rem] bg-gradient-to-br from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-700 shadow-2xl shadow-orange-500/30 transition-all duration-700 hover:scale-[1.03] hover:rotate-1 overflow-hidden flex flex-col items-center text-center"
                          >
                            {/* Cinematic Sparkle Overlay */}
                            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-30 pointer-events-none" />
                            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/20 to-transparent pointer-events-none" />
                            
                            {/* Floating Trophy Icon */}
                            <div className="relative mb-6">
                              <div className="w-24 h-24 rounded-[2rem] bg-white/20 backdrop-blur-xl flex items-center justify-center shadow-2xl border border-white/30 animate-bounce duration-[3s]">
                                <Trophy className="w-12 h-12 text-white drop-shadow-[0_5px_15px_rgba(255,255,255,0.5)]" />
                              </div>
                              <div className="absolute -top-2 -right-2 w-10 h-10 bg-yellow-300 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                                <Sparkles className="w-6 h-6 text-orange-600" />
                              </div>
                            </div>
                            
                            {/* Winner Name - High Impact */}
                            <div className="relative z-10 space-y-1">
                              <p className="text-[10px] font-black text-white/70 uppercase tracking-[0.4em] mb-2 leading-none">Afortunado Ganador</p>
                              <h4 className="text-3xl md:text-4xl font-[1000] text-white uppercase italic tracking-tighter leading-none drop-shadow-2xl">
                                {ganador.nombre || ganador.email}
                              </h4>
                            </div>

                            <div className="mt-6 flex flex-wrap justify-center gap-3 relative z-10">
                              <Badge className="bg-white/20 backdrop-blur-md text-white border-white/20 font-black text-[12px] px-6 py-2 rounded-full uppercase tracking-widest italic">
                                Boleto #{ganador.numero_boleto}
                              </Badge>
                              {sorteo.tipo === 'etapas' && (ganador.etapa || ganador.etapa_numero) && (
                                <Badge className="bg-black/20 backdrop-blur-md text-white border-white/10 font-black text-[12px] px-6 py-2 rounded-full uppercase tracking-widest italic">
                                  Etapa {ganador.etapa || ganador.etapa_numero}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="mt-8 w-full p-6 bg-white/10 dark:bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/20 relative z-10 shadow-inner">
                              <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-2">Premio Conquistado</p>
                              <p className="text-xl md:text-2xl font-black text-white uppercase italic tracking-tight">
                                {ganador.premio || ganador.prize_name}
                              </p>
                            </div>

                            {ganador.fecha_seleccion && (
                              <div className="mt-6 flex items-center gap-3 text-[10px] font-black text-white/50 uppercase tracking-[0.2em] relative z-10">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>{new Date(ganador.fecha_seleccion).toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                                <span className="opacity-30">•</span>
                                <Clock className="w-3.5 h-3.5" />
                                <span>{new Date(ganador.fecha_seleccion).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-20 bg-slate-50 dark:bg-white/5 rounded-[3rem] border border-dashed border-slate-200 dark:border-white/10 animate-in fade-in duration-700">
                        <div className="w-20 h-20 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                           <Trophy className="w-10 h-10 text-slate-300 dark:text-slate-700" />
                        </div>
                        <h3 className="text-xl font-black text-slate-800 dark:text-white uppercase italic tracking-tighter mb-2">Aún no hay ganadores</h3>
                        <p className="text-slate-400 text-sm font-medium">¡Tú podrías ser el primero en aparecer aquí!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="lg:col-span-1">
            <Card className="sorteo-card sticky top-4" data-testid="compra-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xl font-black uppercase tracking-tight">🎟 Comprar Boletos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                {(sorteo.estado === 'published' || sorteo.estado === 'activo' || sorteo.estado === 'waiting') && boletosDisponibles > 0 ? (
                  sorteo.ventas_pausadas ? (
                    <div className="p-8 bg-orange-50 border-2 border-orange-200 rounded-2xl text-center space-y-4 animate-in fade-in zoom-in duration-300">
                      <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2 shadow-inner">
                        <XCircle className="w-12 h-12 text-orange-600 animate-pulse" />
                      </div>
                      <h3 className="text-2xl font-bold text-orange-900">Ventas Pausadas</h3>
                      <p className="text-gray-700 leading-relaxed">
                        El administrador ha pausado temporalmente las ventas para este sorteo.
                        No te preocupes, esto es normal y podrás comprar muy pronto.
                      </p>
                      <div className="pt-2">
                        <Badge variant="outline" className="bg-orange-200/50 text-orange-800 border-orange-300 py-1.5 px-4 font-semibold">
                          Próximamente disponible
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <>
                      {paqueteData ? (
                        // ── PAQUETE ACTIVO ──────────────────────────────────────
                        <div className="space-y-4 animate-in fade-in duration-300">
                          {/* Hero del paquete */}
                          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-5 shadow-xl shadow-orange-500/20">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <div className="relative z-10">
                              <p className="text-[9px] font-black text-orange-100/80 uppercase tracking-[0.3em] mb-1">Paquete Activo</p>
                              <div className="flex items-end justify-between">
                                <div>
                                  <span className="text-4xl font-black text-white leading-none">{paqueteData.cantidad + (paqueteData.boletos_gratis || 0)}</span>
                                  <span className="text-sm font-bold text-orange-100 ml-2">boletos</span>
                                </div>
                                {paqueteData.descuento > 0 && (
                                  <Badge className="bg-white text-orange-600 font-black text-xs px-3 py-1 shadow-lg">-{paqueteData.descuento}% OFF</Badge>
                                )}
                              </div>
                              <div className="mt-3 pt-3 border-t border-white/20 grid grid-cols-3 gap-2 text-center">
                                <div>
                                  <p className="text-[10px] text-orange-100/60 font-bold uppercase">Pagados</p>
                                  <p className="text-lg font-black text-white">{paqueteData.cantidad}</p>
                                </div>
                                {(paqueteData.boletos_gratis || 0) > 0 && (
                                  <div>
                                    <p className="text-[10px] text-green-200/80 font-bold uppercase">Gratis</p>
                                    <p className="text-lg font-black text-green-300">+{paqueteData.boletos_gratis}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-[10px] text-orange-100/60 font-bold uppercase">Precio</p>
                                  <p className="text-base font-black text-white">{formatCurrency(paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Chips de números pre-asignados */}
                          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] overflow-hidden">
                            <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-slate-200/60 dark:border-white/5">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Números reservados</span>
                              </div>
                              <Badge variant="outline" className="text-[9px] h-5 px-2 font-black border-green-500/30 text-green-600 dark:text-green-400">
                                {numerosBoletos.filter(n => n).length} boletos
                              </Badge>
                            </div>
                            <div className="p-3 max-h-36 overflow-y-auto scrollbar-premium">
                              <div className="flex flex-wrap gap-1.5">
                                {numerosBoletos.filter(n => n).map((n, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white dark:bg-white/10 border border-orange-200/60 dark:border-orange-500/20 text-[11px] font-black text-orange-600 dark:text-orange-400 shadow-sm hover:scale-105 transition-transform">
                                    #{n}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <p className="px-4 pb-3 text-[9px] text-muted-foreground/50 italic text-center">Reservados automáticamente por el sistema</p>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-[10px] text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-white/5 font-black uppercase tracking-widest h-9 rounded-xl border border-orange-200/50 dark:border-orange-500/20"
                            onClick={() => {
                              setPaqueteId(null);
                              setPaqueteData(null);
                              setCantidad(sorteo.cantidad_minima_boletos || 1);
                            }}
                          >
                            ✕ Quitar paquete y elegir manual
                          </Button>
                        </div>
                      ) : (
                        // ── SELECCIÓN MANUAL ────────────────────────────────────
                        <div className="space-y-5 animate-in fade-in duration-300">

                          {/* A — Cantidad con stepper ± */}
                          <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-black">A</span>
                              ¿Cuántos boletos?
                            </Label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => { const min = sorteo.cantidad_minima_boletos || 1; if (cantidad > min) handleCantidadChange(cantidad - 1); }}
                                className="w-11 h-11 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 flex items-center justify-center text-xl font-black hover:bg-orange-50 hover:border-orange-300 dark:hover:bg-orange-500/10 transition-all active:scale-95"
                              >−</button>
                              <Input
                                id="cantidad"
                                type="number"
                                min={sorteo.cantidad_minima_boletos || 1}
                                max={Math.min(boletosDisponibles, LIMITE_POR_COMPRA)}
                                value={cantidad}
                                onChange={(e) => handleCantidadChange(e.target.value)}
                                className="flex-1 h-11 text-center text-xl font-black rounded-xl border-slate-200 dark:border-white/10"
                                data-testid="cantidad-input"
                              />
                              <button
                                type="button"
                                onClick={() => { const max = Math.min(boletosDisponibles, LIMITE_POR_COMPRA); if (cantidad < max) handleCantidadChange(cantidad + 1); }}
                                className="w-11 h-11 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 flex items-center justify-center text-xl font-black hover:bg-orange-50 hover:border-orange-300 dark:hover:bg-orange-500/10 transition-all active:scale-95"
                              >+</button>
                            </div>
                            <p className="text-[10px] text-muted-foreground font-bold text-center">
                              {sorteo.cantidad_minima_boletos > 1
                                ? `Mínimo ${sorteo.cantidad_minima_boletos} · Máximo ${LIMITE_POR_COMPRA}`
                                : `Hasta ${Math.min(boletosDisponibles, LIMITE_POR_COMPRA)} disponibles`}
                            </p>
                          </div>

                          {/* B — Chips editables de números */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-black">B</span>
                                Tus números
                              </Label>
                              <button
                                type="button"
                                onClick={() => setNumerosBoletos(asignarNumerosAleatorios(cantidad))}
                                className="text-[9px] font-black uppercase tracking-widest text-orange-500 hover:text-orange-600 transition-colors"
                              >🎲 Aleatorios</button>
                            </div>
                            <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] overflow-hidden">
                              <div className={`p-3 ${numerosBoletos.length > 8 ? 'max-h-44 overflow-y-auto scrollbar-premium' : ''}`}>
                                <div className="flex flex-wrap gap-1.5">
                                  {numerosBoletos.map((numero, index) => (
                                    <input
                                      key={index}
                                      type="number"
                                      min="1"
                                      max={sorteo.cantidad_total_boletos}
                                      value={numero}
                                      onChange={(e) => handleNumeroChange(index, e.target.value)}
                                      placeholder={`#${index + 1}`}
                                      data-testid={`numero-boleto-input-${index}`}
                                      className={`w-14 h-9 text-center text-[12px] font-black rounded-xl border transition-all outline-none
                                      ${numero
                                          ? 'bg-white dark:bg-orange-500/10 border-orange-300 dark:border-orange-500/30 text-orange-600 dark:text-orange-400 shadow-sm'
                                          : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-foreground'
                                        }
                                      focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20
                                      [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="px-4 pb-2 pt-1 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between">
                                <p className="text-[9px] text-muted-foreground/60 italic">Rango: 1–{sorteo.cantidad_total_boletos}</p>
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">
                                  {numerosBoletos.filter(n => n && n !== '').length}/{numerosBoletos.length} completados
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ── RESUMEN DE COMPRA ─────────────────────── */}
                      <div className="rounded-3xl border border-slate-200 dark:border-white/10 overflow-hidden">
                        <div className="bg-slate-50 dark:bg-white/[0.04] px-5 py-3 border-b border-slate-200 dark:border-white/5">
                          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-black">{paqueteData ? 'B' : 'C'}</span>
                            Resumen de compra
                          </p>
                        </div>
                        <div className="p-5 space-y-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground font-bold">
                              {paqueteData ? `${paqueteData.cantidad + (paqueteData.boletos_gratis || 0)} boletos` : `${cantidad} boleto${cantidad !== 1 ? 's' : ''}`}
                            </span>
                            <span className="font-black">
                              {paqueteData ? formatCurrency(paqueteData.cantidad * sorteo.precio_boleto) : formatCurrency(sorteo.precio_boleto * cantidad)}
                            </span>
                          </div>
                          {paqueteData && paqueteData.descuento > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-green-600 dark:text-green-400 font-bold flex items-center gap-1.5">
                                <span className="text-[9px] bg-green-500/10 text-green-600 border border-green-500/20 px-2 py-0.5 rounded font-black uppercase">-{paqueteData.descuento}% OFF</span>
                                Descuento
                              </span>
                              <span className="font-black text-green-600 dark:text-green-400">-{formatCurrency(paqueteData.cantidad * sorteo.precio_boleto * (paqueteData.descuento / 100))}</span>
                            </div>
                          )}
                          {paqueteData && (paqueteData.boletos_gratis || 0) > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-green-600 dark:text-green-400 font-bold flex items-center gap-1.5">
                                <span className="text-[9px] bg-green-500/10 text-green-600 border border-green-500/20 px-2 py-0.5 rounded font-black uppercase">GRATIS</span>
                                {paqueteData.boletos_gratis} boletos de regalo
                              </span>
                              <span className="font-black text-green-600 dark:text-green-400">-{formatCurrency(paqueteData.boletos_gratis * sorteo.precio_boleto)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-[11px] text-muted-foreground">
                            <span className="font-bold">Precio por boleto</span>
                            <span className="font-black">{formatCurrency(sorteo.precio_boleto)}</span>
                          </div>
                          <div className="h-px bg-slate-200 dark:bg-white/5" />
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Total a pagar</p>
                              {paqueteData && (paqueteData.descuento > 0 || (paqueteData.boletos_gratis || 0) > 0) && (
                                <p className="text-[11px] line-through text-muted-foreground/50 font-bold">
                                  {formatCurrency((paqueteData.cantidad + (paqueteData.boletos_gratis || 0)) * sorteo.precio_boleto)}
                                </p>
                              )}
                            </div>
                            <span className="text-3xl font-black" style={{ color: sorteo.color_primario || '#f97316' }}>
                              {paqueteData
                                ? formatCurrency(paqueteData.cantidad * sorteo.precio_boleto * (1 - (paqueteData.descuento / 100)))
                                : formatCurrency(sorteo.precio_boleto * cantidad)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* ── CTA ───────────────────────────────────────── */}
                      <div className="space-y-3 pt-1">
                        <AppButton
                          className="w-full h-14 rounded-2xl font-black uppercase tracking-wider text-sm shadow-xl"
                          style={{ backgroundColor: sorteo.color_primario || '#f97316' }}
                          onClick={handleVerDatosBancarios}
                          disabled={(!paqueteId && numerosBoletos.some(n => !n)) || validandoBoletos}
                          loading={validandoBoletos}
                          data-testid="ver-datos-bancarios-btn"
                          icon={CheckCircle}
                        >
                          {validandoBoletos ? 'Validando boletos...' : 'Continuar con la compra'}
                        </AppButton>
                        <p className="text-[10px] text-center text-muted-foreground font-bold flex items-center justify-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                          Compra segura · Transferencia, Tarjeta o PayPal
                        </p>
                      </div>

                      <FastCheckoutModal
                        isOpen={showDatosBancarios}
                        onClose={() => setShowDatosBancarios(false)}
                        sorteo={sorteo}
                        paqueteId={paqueteId}
                        paqueteData={paqueteData}
                        cantidad={cantidad}
                        numerosBoletos={numerosBoletos}
                      />

                      {vendedorLink && (
                        <p className="text-xs text-center text-gray-600">
                          Compra referida por vendedor
                        </p>
                      )}
                    </>
                  )
                ) : sorteo.estado === 'live' ? (
                  <div className="space-y-6">
                    <LiveAnimation sorteo={sorteo} user={user} />
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-600 font-semibold mb-2">
                      {sorteo.estado === 'completado' ? 'Sorteo Finalizado' : 'Sorteo no disponible'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {boletosDisponibles === 0 ? 'Todos los boletos han sido vendidos' : ''}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Promotional Banners for Other Active Raffles */}
        {otrosSorteos.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-6 text-center">Otros Sorteos Activos</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otrosSorteos.map((otroSorteo) => (
                <Card
                  key={otroSorteo.id}
                  className="sorteo-card cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/sorteo/${otroSorteo.landing_slug}`)}
                >
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    {otroSorteo.imagenes && otroSorteo.imagenes.length > 0 ? (
                      <img
                        src={getMediaUrl(otroSorteo.imagenes[0])}
                        alt={otroSorteo.titulo}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: otroSorteo.color_primario }}
                      >
                        <Trophy className="w-16 h-16 text-white opacity-30" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge style={{ backgroundColor: otroSorteo.color_secundario }}>
                        {otroSorteo.tipo === 'etapas' ? 'Por Etapas' : 'Único'}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg mb-2 line-clamp-2">{otroSorteo.titulo}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{otroSorteo.descripcion}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold">
                        {formatCurrency(otroSorteo.precio_boleto)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {otroSorteo.progreso_porcentaje.toFixed(0)}% vendido
                      </span>
                    </div>
                    <Button
                      className="w-full mt-3"
                      size="sm"
                      style={{ backgroundColor: otroSorteo.color_primario }}
                    >
                      Ver Sorteo
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SorteoLanding;

